"use client";

import React from "react";
import { BlurFade } from "@/components/ui/blur-fade";
import { Info, Lightbulb, AlertTriangle, Quote } from "lucide-react";

interface CalloutBlockProps {
  title?: string;
  content: string;
  variant?: string;
}

const VARIANT_CONFIG: Record<string, { icon: React.ComponentType<any>; bg: string; border: string; iconColor: string }> = {
  info: {
    icon: Info,
    bg: "bg-blue-50",
    border: "border-blue-200",
    iconColor: "text-blue-500",
  },
  tip: {
    icon: Lightbulb,
    bg: "bg-green-50",
    border: "border-green-200",
    iconColor: "text-green-500",
  },
  warning: {
    icon: AlertTriangle,
    bg: "bg-amber-50",
    border: "border-amber-200",
    iconColor: "text-amber-500",
  },
  quote: {
    icon: Quote,
    bg: "bg-muted/50",
    border: "border-foreground/10",
    iconColor: "text-foreground/30",
  },
};

const CalloutBlock = ({ title, content, variant = "info" }: CalloutBlockProps) => {
  if (!content) return null;

  const config = VARIANT_CONFIG[variant] || VARIANT_CONFIG.info;
  const Icon = config.icon;

  return (
    <section className="py-8 px-6 bg-white">
      <div className="container max-w-4xl mx-auto">
        <BlurFade delay={0.1}>
          <div className={`rounded-2xl border ${config.border} ${config.bg} p-6 md:p-8 flex gap-4`}>
            <div className="flex-shrink-0 mt-0.5">
              <Icon size={22} className={config.iconColor} strokeWidth={1.5} />
            </div>
            <div className="space-y-2">
              {title && (
                <h3 className="text-base font-bold text-foreground">
                  {title}
                </h3>
              )}
              <p className="text-foreground/70 text-sm leading-relaxed">
                {content}
              </p>
            </div>
          </div>
        </BlurFade>
      </div>
    </section>
  );
};

export default CalloutBlock;
